<template>
  <div class="vip-assets-page">
    <div class="content-header">
      <button
        class="btn-back"
        title="Back to VIP CMS"
        @click="goBack"
      >
        <md-icon>arrow_back</md-icon>
      </button>
      <div class="content-header__title">
        <h3>VIP Assets</h3>
        <p>Images used by VIP pages. Copy an image path to use it in a page.</p>
      </div>
      <div class="add-section">
        <label class="upload-label">
          <img src="@/assets/add-button.svg" class="add-button__new">
          <span>Upload Asset</span>
          <input
            ref="fileInput"
            type="file"
            accept="image/*,.svg"
            style="display: none"
            @change="onFileChange"
          >
        </label>
      </div>
    </div>

    <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>

    <div class="c-table-wrapper">
      <div class="c-table__head">
        <div class="c-table__cell">
          Id
        </div>
        <div class="c-table__cell">
          Original Name
        </div>
        <div class="c-table__cell c-table__cell--actions" />
      </div>
      <div class="c-table__body">
        <template v-if="assets.length">
          <div v-for="asset in assets" :key="asset.id" class="c-table__row">
            <div class="c-table__cell">
              {{ asset.id }}
            </div>
            <div class="c-table__cell">
              {{ asset.originalName }}
            </div>
            <div class="c-table__cell c-table__cell--actions">
              <button
                title="Copy Image Path"
                class="action-btn"
                @click="copyImagePath(asset.imagePath)"
              >
                <md-icon>content_copy</md-icon>
              </button>
              <button
                title="Delete"
                class="action-btn action-btn--danger"
                @click="removeAsset(asset.id)"
              >
                <img src="@/assets/delete-button.svg" class="action-btn__img" alt="">
              </button>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="c-table__row empty-row">
            <div class="c-table__cell empty-message">
              No VIP Assets found
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { vipService } from '@/services/vip';
import config from "../../../../../../config/local";
import ConfirmationModalComponent from '@/shared/confirmation-modal/ConfirmationModalComponent';
import { onlinePermissionsMixin } from '../../../../../mixins/onlinePermissionsMixin';

export default {
  name: 'VipAssetsPage',

  components: {
    // eslint-disable-next-line vue/no-unused-components
    ConfirmationModalComponent,
  },

  mixins: [onlinePermissionsMixin],

  data() {
    return {
      websiteId: null,
      assets: [],
      errorMessage: '',
    };
  },

  created() {
    this.websiteId = this.$route.params.websiteId;
    if (!this.canReadWebsite(this.websiteId)) {
      this.$router.push({ name: 'on-line-vip' });
    }
    this.loadAssets();
  },

  methods: {
    ...mapActions('response', ['handleSuccess']),

    async loadAssets() {
      try {
        const response = await vipService.getVipAssets(this.websiteId);
        this.assets = response.data || [];
      } catch (error) {
        this.$router.push({ name: 'on-line-vip' });
      }
    },

    async onFileChange(event) {
      const file = event.target.files[0];
      if (!file) {
        return;
      }

      try {
        const formData = new FormData();
        formData.append('file', file, file.name);
        await vipService.uploadVipAsset(this.websiteId, formData);
        this.handleSuccess({ message: 'Asset successfully uploaded!' });
        await this.loadAssets();
      } catch (error) {
        this.errorMessage = error?.data?.message || 'Error uploading asset';
      } finally {
        this.$refs.fileInput.value = '';
      }
    },

    removeAsset(id) {
      this.$modal.show(
        ConfirmationModalComponent,
        {
          confirmationMsg: 'Are you sure you want to delete this asset?',
          acceptButtonText: 'DELETE',
          onAccept: () => this.confirmRemoveAsset(id),
          onClose: () => {},
        },
        { height: 'auto', scrollable: false, width: "440px" }
      );
    },

    async confirmRemoveAsset(id) {
      try {
        await vipService.deleteVipAsset(id);
        await this.loadAssets();
      } catch (error) {
        this.errorMessage = error?.data?.message || 'Error deleting asset';
      }
    },

    copyImagePath(imagePath) {
      navigator.clipboard.writeText(`https://img.meridianbet.com/cdn-cgi/image/height=70,quality=80/${config.BASE_URL}/vip-assets/${imagePath}`);
      this.handleSuccess({ message: 'Image path copied!' });
    },

    goBack() {
      this.$router.push({ name: 'on-line-vip' });
    },
  },
};
</script>

<style lang="scss" scoped>
// same delete icon as every other table/list (src/assets/delete-button.svg)
.action-btn__img {
  display: block;
  flex-shrink: 0;
  width: 20px;
  min-width: 20px;
  height: 20px;
}

.vip-assets-page {
  font-family: var(--font-sans);

  .empty-message {
    text-align: center;
    color: var(--text-muted);
  }

  .error-message {
    margin: 0 24px 16px;
    padding: 10px 14px;
    border: 1px solid var(--danger-border);
    border-radius: var(--radius-md);
    background-color: var(--danger-bg);
    color: var(--danger-fg);
    font-size: 13px;
  }

  .content-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px 24px;
    border-bottom: 1px solid var(--border-default);

    .btn-back {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border: none;
      border-radius: var(--radius-md);
      background: none;
      color: var(--text-secondary);
      cursor: pointer;

      &:hover {
        background-color: var(--bg-hover);
        color: var(--text-primary);
      }

      .md-icon {
        color: inherit !important;
      }
    }

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: var(--text-primary);
    }

    p {
      margin: 2px 0 0;
      font-size: 13px;
      color: var(--text-secondary);
    }
  }

  .content-header__title {
    flex: 1;
    min-width: 0;
  }

  // the whole red button is the upload target (label wraps the hidden file input)
  .upload-label {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    color: inherit;
    cursor: pointer;

    .add-button__new {
      cursor: pointer;
    }
  }

  // stays a table cell (rows are display: table); buttons sit inline on the right
  .c-table__cell--actions {
    width: 120px;
    text-align: right;
    white-space: nowrap;
  }

  // icon buttons like the other tables' row actions
  .action-btn {
    display: inline-flex;
    vertical-align: middle;
    margin-left: 4px;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: var(--radius-md);
    background: none;
    color: var(--text-muted);
    cursor: pointer;

    .md-icon {
      color: inherit !important;
      font-size: 18px !important;
    }

    &:hover {
      background-color: var(--bg-hover);
      color: var(--text-primary);
    }

    &--danger {
      color: var(--danger-fg);

      &:hover {
        background-color: var(--danger-bg);
        color: var(--danger-fg);
      }
    }
  }
}
</style>
